import {makeObservable, observable, action, computed} from 'mobx';

// EvolveRun 场景的步骤配置（两步流程）
export const DEFAULT_STEPS: Step[] = [
    {
        id: 0,
        titles: '准备原始工程文件',
        descriptions: '打开或者编写你的原始开发工程，并从中选择要优化的目标算法文件及评估器，评估器将对优化中产生的所有算法进行评分。',
        completed: false
    },
    {
        id: 1,
        titles: '完善你的算法优化任务',
        descriptions: '请详细描述你的算法背景和优化诉求，并设定相关优化参数。',
        completed: false
    }
];

class StepStore {
    job_id: string;
    steps: Step[] = [];
    currentStep = 0; // 从 0 开始
    viewingStep = 0; // 当前查看的步骤 (用于在第3步时查看其他步骤)
    isViewMode = false; // 是否处于查看模式

    /** 步骤映射表：UI步骤 -> 后端步骤（两步流程） */
    private stepMapping: Record<number, number> = {
        0: 0,
        1: 1,
        2: 2,
        3: 3,
        4: 4
    };

    constructor(job_id: string, customSteps: Step[] = DEFAULT_STEPS) {
        this.job_id = job_id;
        this.steps = customSteps ?? DEFAULT_STEPS;

        makeObservable(this, {
            steps: observable, // 可观察数组
            currentStep: observable, // 可观察状态
            viewingStep: observable, // 可观察状态
            isViewMode: observable, // 可观察状态
            totalSteps: computed, // 计算属性
            currentStepInfo: computed, // 计算属性
            apiStep: computed, // 计算属性
            displayStepInfo: computed, // 计算属性
            setStepMapping: action.bound, // 修改 state
            initSteps: action.bound, // 修改 state
            nextStep: action.bound,
            prevStep: action.bound,
            gotoStep: action.bound,
            markCompleted: action.bound,
            reset: action.bound,
            setcurrentStep: action.bound,
            setViewingStep: action.bound,
            enterViewMode: action.bound,
            exitViewMode: action.bound
        });
    }

    get totalSteps() {
        return this.steps.length;
    }

    get currentStepInfo() {
        return this.steps.find(s => s.id === this.currentStep);
    }

    // 根据查看模式返回对应的步骤信息
    get displayStepInfo() {
        const targetStep = this.isViewMode ? this.viewingStep : this.currentStep;
        return this.steps.find(s => s.id === targetStep);
    }

    /** 获取传给后端的实际步骤值 */
    get apiStep(): number {
        return this.stepMapping[this.currentStep] ?? this.currentStep;
    }
    /** 设置步骤映射（用于 EvolveRunModule 场景） */
    setStepMapping(mapping: Record<number, number>) {
        this.stepMapping = mapping;
    }

    setcurrentStep = (stepId: number | string) => {
        this.currentStep = Number(stepId);
    };

    // 初始化步骤
    initSteps(stepTitles: string[]) {
        this.steps = stepTitles?.map((titles, idx) => ({
            id: idx + 1,
            titles,
            descriptions: '',
            completed: false
        }));
        this.currentStep = 0;
    }

    nextStep() {
        if (this.currentStep < this.totalSteps) {
            this.markCompleted(this.currentStep);
            this.currentStep++;
        }
    }

    prevStep() {
        if (this.currentStep > 0) {
            this.currentStep--;
        }
    }

    gotoStep(stepId: number) {
        if (stepId >= 0 && stepId <= this.totalSteps) {
            this.currentStep = stepId;
        }
    }

    markCompleted(stepId: number, flag = true) {
        const step = this.steps.find(s => s.id === stepId);
        if (step) {
            step.completed = flag;
        }
    }

    reset() {
        this.steps.forEach(s => (s.completed = false));
        this.currentStep = 0;
    }

    setViewingStep(stepId: number) {
        if (stepId >= 0 && stepId < this.totalSteps) {
            this.viewingStep = stepId;
        }
    }

    enterViewMode(stepId: number) {
        this.isViewMode = true;
        this.viewingStep = stepId;
    }

    exitViewMode() {
        this.isViewMode = false;
        this.viewingStep = this.currentStep;
    }
}

export default StepStore;
